import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBuilding, FaCheckCircle, FaCalendarAlt } from "react-icons/fa";
import {
  MdEdit,
  MdDashboard,
  MdCompare,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";
import { useAuth } from "../contexts/AuthContext";
import Footer from "../components/Footer";
import { SEO } from "../components/SEO";
import { generateFAQSchema } from "../utils/seo.utils";
import { webApplicationSchema } from "../config/seo.config";

// FAQ項目の型定義
interface FAQItem {
  question: string;
  answer: string;
}

// 機能の詳細情報の型定義
interface FeatureDetail {
  title: string;
  icon: React.ComponentType<any>;
  color: string;
  description: string;
  details: string[];
  imagePlaceholder: string;
  imagePath?: string; // スクリーンショット画像のパス（オプション）
}

const AboutPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<FeatureDetail | null>(
    null
  );

  // FAQデータ
  const faqs: FAQItem[] = [
    {
      question: "就活ダッシュボードは無料で使えますか？",
      answer:
        "はい、完全無料でご利用いただけます。登録料や月額料金は一切かかりません。Googleアカウントでログインするだけで、すぐに全ての機能をお使いいただけます。",
    },
    {
      question: "どのような情報を管理できますか？",
      answer:
        "企業情報（企業名、業界、マイページのURL・ID・パスワード）、応募状況（選考ステータス）、スケジュール（ES締切、説明会、面接など）、企業ごとのメモを一元管理できます。",
    },
    {
      question: "複数の企業を同時に管理できますか？",
      answer:
        "はい、登録できる企業数に制限はありません。何社でも登録して管理することができます。",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  // 機能詳細データ
  const features: FeatureDetail[] = [
    {
      title: "企業管理",
      icon: FaBuilding,
      color: "indigo",
      description:
        "気になる企業の情報を一箇所に集約。企業名、業界、マイページのURL・ID・パスワードなど、必要な情報を簡単に登録・管理できます。",
      details: [
        "企業情報の登録・編集・削除",
        "マイページのログイン情報管理",
        "企業ごとのメモ機能",
      ],
      imagePlaceholder: "企業一覧画面のプレビュー",
      imagePath: "/images/company-detail.png",
    },
    {
      title: "応募状況管理",
      icon: FaCheckCircle,
      color: "green",
      description:
        "選考フローの進捗を視覚的に把握。エントリーから内定まで、各ステージの状況を簡単に更新・追跡できます。",
      details: [
        "選考ステータスの更新",
        "応募状況の一覧表示",
        "ステータス別の集計表示",
        "ダッシュボードでの進捗確認",
      ],
      imagePlaceholder: "応募状況画面のプレビュー",
      imagePath: "/images/company.png",
    },
    {
      title: "スケジュール管理",
      icon: FaCalendarAlt,
      color: "orange",
      description:
        "ES締切、説明会、面接など、重要な予定を忘れずに管理。カレンダー形式で一目で確認できます。",
      details: ["予定の登録・編集・削除", "企業ごとのスケジュール管理"],
      imagePlaceholder: "スケジュール画面のプレビュー",
      imagePath: "/images/schedule.png",
    },
    {
      title: "ESテンプレート保存機能",
      icon: MdEdit,
      color: "purple",
      description:
        "題目ごとにエントリーシートのテンプレートを保存できます。題目ごとに複数のテンプレートを保存できるので、企業ごとに使い分けが可能です。",
      details: [
        "題目ごとに複数のテンプレート保存",
        "保存したテンプレートの編集・削除",
      ],
      imagePlaceholder: "ESテンプレート保存機能画面のプレビュー",
      imagePath: "/images/template.png",
    },
    {
      title: "ダッシュボード",
      icon: MdDashboard,
      color: "blue",
      description:
        "登録企業数、応募数、スケジュール数を一目で把握。選考ステータスの内訳も視覚的に確認できます。",
      details: [
        "主要指標の可視化",
        "ステータス別の集計",
        "今日・今週のタスク表示",
      ],
      imagePlaceholder: "ダッシュボード画面のプレビュー",
      imagePath: "/images/dashboard1.png",
    },
    {
      title: "企業比較機能",
      icon: MdCompare,
      color: "teal",
      description:
        "複数の企業を選択して並べて比較できます。年収範囲、福利厚生、勤務地などを一目で比較し、志望企業の選定に役立てましょう。",
      details: [
        "複数企業の同時選択",
        "年収・福利厚生・勤務地・従業員数の比較",
        "最大5社まで同時比較可能",
      ],
      imagePlaceholder: "企業比較画面のプレビュー",
      imagePath: "/images/compare.png",
    },
  ];

  // FAQスキーマの生成
  const faqSchema = generateFAQSchema(faqs);

  return (
    <>
      <SEO
        title="就活ダッシュボードについて"
        description="就活ダッシュボードは新卒就職活動を効率化する無料ツールです。企業情報、選考状況、面接スケジュールを一元管理。ES締切も逃さず、内定までサポート。"
        canonical="/about"
        keywords={[
          "就活管理",
          "無料ツール",
          "新卒",
          "就活サポート",
          "企業研究",
        ]}
        structuredData={[faqSchema, webApplicationSchema]}
      />
      <div className="min-h-screen bg-gray-50">
        {/* ヘッダーバー */}
        <header className="bg-white border-b border-gray-200 py-3 md:py-4 shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center">
            <div>
              <h1 className="m-0 text-lg md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-indigo-500 to-purple-600">
                就活ダッシュボード
              </h1>
              <p className="m-0 text-xs text-gray-500 mt-1 hidden sm:block">
                就活企業とスケジュールを管理
              </p>
            </div>
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="py-2 px-3 md:px-6 bg-indigo-600 text-white border-none rounded-md text-xs md:text-sm font-medium cursor-pointer transition-colors hover:bg-indigo-700"
              >
                ダッシュボード
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="py-2 px-3 md:px-6 bg-indigo-600 text-white border-none rounded-md text-xs md:text-sm font-medium cursor-pointer transition-colors hover:bg-indigo-700"
              >
                ログイン
              </button>
            )}
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 md:p-8">
          {/* ヒーローセクション */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl md:rounded-2xl p-6 md:p-12 mb-8 md:mb-12 text-white shadow-xl">
            <h1 className="text-3xl md:text-5xl font-bold mb-2 m-0">
              就活ダッシュボード
            </h1>
            <p className="text-base md:text-xl mb-4 md:mb-6 opacity-90 leading-relaxed">
              就活企業とスケジュールを管理
            </p>
            <p className="text-sm md:text-lg opacity-90 leading-relaxed max-w-3xl mb-6 md:mb-8">
              企業情報、応募状況、スケジュールを一元管理。
              <br className="hidden md:block" />
              あなたの就活をサポートします。
            </p>
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="py-2.5 md:py-3 px-6 md:px-8 bg-white text-indigo-600 border-none rounded-md text-sm md:text-base font-bold cursor-pointer transition-all hover:bg-gray-100 hover:scale-105 shadow-lg"
              >
                ダッシュボードへ
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="py-2.5 md:py-3 px-6 md:px-8 bg-white text-indigo-600 border-none rounded-md text-sm md:text-base font-bold cursor-pointer transition-all hover:bg-gray-100 hover:scale-105 shadow-lg"
              >
                今すぐ始める
              </button>
            )}
          </div>

          {/* 主要機能 */}
          <section className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8 text-center">
              主要機能
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedFeature(feature)}
                    className="bg-white rounded-lg md:rounded-xl p-5 md:p-8 shadow-sm border border-gray-200 transition-all hover:-translate-y-1 hover:shadow-lg text-left w-full cursor-pointer"
                  >
                    <div
                      className={`text-4xl md:text-5xl mb-4 md:mb-6 text-${feature.color}-500`}
                    >
                      <Icon />
                    </div>
                    <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {feature.description}
                    </p>
                    <p className="text-sm text-indigo-600 font-medium">
                      クリックして詳細を見る →
                    </p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* 使い方の流れ */}
          <section className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8 text-center">
              使い方の流れ
            </h2>
            <div className="bg-white rounded-lg md:rounded-xl p-5 md:p-8 shadow-sm border border-gray-200">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      企業を登録
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      気になる企業の情報を登録しましょう。企業名、業界、マイページのログイン情報などを入力できます。
                    </p>
                  </div>
                </div>

                <div className="border-l-2 border-gray-200 ml-5 h-8"></div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      応募状況を更新
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      エントリーしたら応募状況を登録。選考が進むたびに、ステータスを更新して進捗を管理しましょう。
                    </p>
                  </div>
                </div>

                <div className="border-l-2 border-gray-200 ml-5 h-8"></div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      スケジュールを管理
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      ES締切、説明会、面接などの予定を登録。カレンダーで確認して、大切な日程を逃さないようにしましょう。
                    </p>
                  </div>
                </div>

                <div className="border-l-2 border-gray-200 ml-5 h-8"></div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      ダッシュボードで確認
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      登録企業数、応募数、スケジュール数をダッシュボードで一目で確認。就活の進捗を可視化しましょう。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQセクション */}
          <section className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8 text-center">
              よくある質問
            </h2>
            <div className="bg-white rounded-lg md:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full text-left p-4 md:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
                    aria-expanded={openFAQIndex === index}
                  >
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 pr-4">
                      {faq.question}
                    </h3>
                    <span className="flex-shrink-0 text-indigo-600 text-xl">
                      {openFAQIndex === index ? (
                        <MdKeyboardArrowUp />
                      ) : (
                        <MdKeyboardArrowDown />
                      )}
                    </span>
                  </button>
                  {openFAQIndex === index && (
                    <div className="px-4 md:px-6 pb-4 md:pb-6 text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* CTAセクション */}
          <section className="text-center mb-8 md:mb-12">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg md:rounded-xl p-6 md:p-10 border border-indigo-100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
                さあ、就活を始めましょう
              </h2>
              <p className="text-sm md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-6 md:mb-8">
                就活ダッシュボードで、あなたの就職活動をスマートに、効率的に。
                すべての情報を一箇所で管理して、内定への道をしっかりサポートします。
              </p>
              {user ? (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="py-3 md:py-4 px-6 md:px-10 bg-indigo-600 text-white border-none rounded-md text-base md:text-lg font-bold cursor-pointer transition-all hover:bg-indigo-700 hover:scale-105 shadow-lg"
                >
                  ダッシュボードへ
                </button>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="py-3 md:py-4 px-6 md:px-10 bg-indigo-600 text-white border-none rounded-md text-base md:text-lg font-bold cursor-pointer transition-all hover:bg-indigo-700 hover:scale-105 shadow-lg"
                >
                  無料で始める
                </button>
              )}
            </div>
          </section>

          {/* プライバシーポリシーと利用規約 */}
          <section className="mb-8 md:mb-12">
            <div className="bg-white rounded-lg md:rounded-xl p-5 md:p-8 shadow-sm border border-gray-200">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 text-center">
                プライバシーとセキュリティ
              </h2>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block py-3 px-8 bg-gray-100 text-gray-800 border border-gray-300 rounded-md text-base font-semibold no-underline transition-colors hover:bg-gray-200 hover:border-gray-400"
                >
                  プライバシーポリシー
                </a>
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block py-3 px-8 bg-gray-100 text-gray-800 border border-gray-300 rounded-md text-base font-semibold no-underline transition-colors hover:bg-gray-200 hover:border-gray-400"
                >
                  利用規約
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* フッター */}
        <Footer />

        {/* 機能詳細モーダル */}
        {selectedFeature && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedFeature(null)}
          >
            <div
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 md:p-8">
                {/* ヘッダー */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div
                      className={`text-4xl md:text-5xl text-${selectedFeature.color}-500`}
                    >
                      <selectedFeature.icon />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                      {selectedFeature.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedFeature(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none p-2"
                    aria-label="閉じる"
                  >
                    ×
                  </button>
                </div>

                {/* 説明 */}
                <p className="text-gray-600 leading-relaxed mb-6">
                  {selectedFeature.description}
                </p>

                {/* 画面プレビュー */}
                <div className="mb-6">
                  {selectedFeature.imagePath ? (
                    <div className="bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                      <img
                        src={selectedFeature.imagePath}
                        alt={`${selectedFeature.title}のスクリーンショット`}
                        className="w-full h-auto"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border border-gray-300">
                      <div className="text-center text-gray-500">
                        <selectedFeature.icon className="text-6xl md:text-8xl mx-auto mb-3" />
                        <p className="text-sm md:text-base">
                          {selectedFeature.imagePlaceholder}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          ※ 実際の画面イメージは近日公開予定
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 詳細機能リスト */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    主な機能
                  </h3>
                  <ul className="space-y-3">
                    {selectedFeature.details.map((detail, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-gray-700"
                      >
                        <span
                          className={`text-${selectedFeature.color}-500 mt-1 flex-shrink-0`}
                        >
                          ✓
                        </span>
                        <span className="leading-relaxed">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 閉じるボタン */}
                <div className="mt-8 flex justify-end">
                  <button
                    onClick={() => setSelectedFeature(null)}
                    className="py-2.5 px-6 bg-indigo-600 text-white border-none rounded-md text-sm md:text-base font-semibold cursor-pointer transition-colors hover:bg-indigo-700"
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AboutPage;
